import React, { useEffect, useState } from 'react';
import { Cloud, Plus, Trash2, Save, PlugZap, Eye, CheckCircle2, XCircle, ChevronDown, ChevronRight, Loader2, CalendarDays, FolderOpen, Table2, ShieldCheck } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { SheetSyncControls } from '../components/SheetSyncControls';
import { GoogleContactsCard } from '../components/GoogleContactsCard';
import { SyncAlertsCard } from '../components/SyncAlertsCard';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const emptySource = (idField) => ({ id: '', label: '', [idField]: '', allowed_classes: ['committee', 'admin'] });

function ClassChips({ selected, onToggle, memberClasses, classLabel, testidPrefix }) {
  return (
    <div className="flex flex-wrap gap-1.5 mt-1">
      {memberClasses.map((mc) => {
        const on = selected.includes(mc.key);
        return (
          <button
            key={mc.key}
            type="button"
            data-testid={`${testidPrefix}-class-${mc.key}`}
            onClick={() => onToggle(mc.key)}
            className="px-2 py-0.5 rounded text-xs font-title transition-colors"
            style={on ? { background: 'var(--navy)', color: 'var(--cream)' } : { background: 'white', color: 'var(--ink)', border: '1px solid rgba(0,0,0,0.18)' }}
          >
            {classLabel(mc.key)}
          </button>
        );
      })}
    </div>
  );
}

export default function GoogleWorkspaceTab() {
  const { getGoogleConfig, updateGoogleConfig, testGoogle, previewGoogleSheet, getGoogleSheetTabs, memberClasses, classLabel } = useAuth();
  const [cfg, setCfg] = useState(null);
  const [saJson, setSaJson] = useState('');
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [err, setErr] = useState('');
  const [showSetup, setShowSetup] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [testBusy, setTestBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewBusy, setPreviewBusy] = useState(false);
  const [tabs, setTabs] = useState(null);
  const [tabsBusy, setTabsBusy] = useState(false);

  useEffect(() => { getGoogleConfig().then(setCfg).catch(() => setCfg(null)); /* eslint-disable-next-line */ }, []);

  if (!cfg) return <div className="flex items-center gap-2 text-sm" style={{ color: 'var(--olive)' }}><Loader2 className="animate-spin" size={16} /> Loading…</div>;

  const setField = (k, v) => setCfg((c) => ({ ...c, [k]: v }));
  const updSource = (list, idx, patch) => setCfg((c) => ({ ...c, [list]: c[list].map((s, i) => (i === idx ? { ...s, ...patch } : s)) }));
  const addSource = (list, idField) => setCfg((c) => ({ ...c, [list]: [...(c[list] || []), emptySource(idField)] }));
  const delSource = (list, idx) => setCfg((c) => ({ ...c, [list]: c[list].filter((_, i) => i !== idx) }));
  const toggleClass = (list, idx, key) => setCfg((c) => ({
    ...c,
    [list]: c[list].map((s, i) => {
      if (i !== idx) return s;
      const on = (s.allowed_classes || []).includes(key);
      return { ...s, allowed_classes: on ? s.allowed_classes.filter((k) => k !== key) : [...(s.allowed_classes || []), key] };
    }),
  }));

  const save = async () => {
    setBusy(true); setErr(''); setSaved(false);
    try {
      const payload = {
        calendars: cfg.calendars, folders: cfg.folders,
        sheet_id: cfg.sheet_id, members_range: cfg.members_range,
        domain_promote_enabled: cfg.domain_promote_enabled,
        promote_domain: cfg.promote_domain, promote_class: cfg.promote_class,
        auto_sync_members: cfg.auto_sync_members, sheet_sync_mode: cfg.sheet_sync_mode,
        sync_alerts_enabled: cfg.sync_alerts_enabled, sync_alert_emails: cfg.sync_alert_emails,
        contacts_enabled: cfg.contacts_enabled, contacts_subject: cfg.contacts_subject, contacts_group_prefix: cfg.contacts_group_prefix,
        contacts_sync_mode: cfg.contacts_sync_mode, auto_sync_contacts: cfg.auto_sync_contacts,
      };
      if (saJson.trim()) payload.service_account_json = saJson.trim();
      const res = await updateGoogleConfig(payload);
      setCfg(res); setSaJson(''); setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Could not save. Check the service account JSON is valid.');
    }
    setBusy(false);
  };

  const runTest = async () => {
    setTestBusy(true); setTestResult(null);
    try { setTestResult(await testGoogle()); }
    catch (e) { setTestResult({ ok: false, error: e?.response?.data?.detail || 'Test failed' }); }
    setTestBusy(false);
  };

  const runPreview = async () => {
    setPreviewBusy(true); setPreview(null);
    try { setPreview(await previewGoogleSheet(8)); }
    catch (e) { setPreview({ error: e?.response?.data?.detail || 'Could not read the sheet' }); }
    setPreviewBusy(false);
  };

  const loadTabs = async () => {
    setTabsBusy(true); setTabs(null);
    try { setTabs(await getGoogleSheetTabs()); }
    catch (e) { setTabs({ error: e?.response?.data?.detail || 'Could not list the tabs' }); }
    setTabsBusy(false);
  };

  const cardStyle = { borderColor: 'rgba(0,0,0,0.08)' };

  return (
    <div className="space-y-6" data-testid="google-workspace-tab">
      <div className="flex items-center gap-2">
        <Cloud size={22} style={{ color: 'var(--maroon)' }} />
        <h2 className="font-title text-xl" style={{ color: 'var(--maroon)' }}>Google Workspace</h2>
        {cfg.service_account_set
          ? <span className="ml-1 inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded" style={{ background: '#e6f4ea', color: '#1a7f37' }}><CheckCircle2 size={13} /> Connected</span>
          : <span className="ml-1 inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded" style={{ background: '#fbeae5', color: '#8a1f11' }}><XCircle size={13} /> Not configured</span>}
      </div>

      {/* Setup instructions */}
      <div className="bg-white rounded-lg border shadow-sm" style={cardStyle}>
        <button type="button" data-testid="gw-setup-toggle" onClick={() => setShowSetup((v) => !v)} className="w-full flex items-center gap-2 px-4 py-3 text-left font-title" style={{ color: 'var(--navy)' }}>
          {showSetup ? <ChevronDown size={16} /> : <ChevronRight size={16} />} Setup instructions (do this once in Google Cloud)
        </button>
        {showSetup && (
          <div className="px-5 pb-5 text-sm space-y-2" style={{ color: 'var(--ink)' }}>
            <ol className="list-decimal ml-5 space-y-1.5">
              <li>Go to <a className="underline" style={{ color: 'var(--navy)' }} href="https://console.cloud.google.com/" target="_blank" rel="noreferrer">Google Cloud Console</a> and create (or pick) a project.</li>
              <li>Under <b>APIs &amp; Services → Library</b>, enable: <b>Google Calendar API</b>, <b>Google Drive API</b>, <b>Google Sheets API</b>.</li>
              <li>Go to <b>IAM &amp; Admin → Service Accounts → Create service account</b> (name it e.g. <i>membership-integrations</i>).</li>
              <li>Open the service account → <b>Keys → Add key → Create new key → JSON</b> and download the file. Paste its full contents in the box below.</li>
              <li>Copy the service account email (looks like <code>…@your-project.iam.gserviceaccount.com</code>).</li>
              <li>For each calendar: Google Calendar → <b>Settings and sharing → Share with specific people</b> → add that email (<i>See all event details</i>, or <i>Make changes</i> if the site should create events). Copy the <b>Calendar ID</b> from <i>Integrate calendar</i>.</li>
              <li>For each Drive folder: open the folder → <b>Share</b> → add that email as <b>Viewer</b>. The folder ID is the part of the URL after <code>/folders/</code>.</li>
              <li>For the members Google Sheet: <b>Share</b> it with that email (Viewer to read, Editor to write). The Sheet ID is the part of the URL between <code>/d/</code> and <code>/edit</code>.</li>
            </ol>
          </div>
        )}
      </div>

      {/* Service account key */}
      <div className="bg-white rounded-lg p-5 border shadow-sm" style={cardStyle}>
        <div className="flex items-center gap-2 mb-2"><ShieldCheck size={16} style={{ color: 'var(--navy)' }} /><h3 className="font-title text-lg" style={{ color: 'var(--navy)' }}>Service account key</h3></div>
        {cfg.client_email && <p className="text-sm mb-2" style={{ color: 'var(--olive)' }}>Connected as <b>{cfg.client_email}</b> — share your calendars/folders/sheet with this email.</p>}
        <Textarea
          data-testid="gw-sa-json"
          value={saJson}
          onChange={(e) => setSaJson(e.target.value)}
          placeholder={cfg.service_account_set ? 'A key is saved. Paste a new JSON key here only to replace it.' : 'Paste the full service-account JSON key here…'}
          className="font-mono text-xs h-32"
        />
        <p className="text-xs mt-1" style={{ color: 'var(--olive)' }}>Stored securely on the server and never shown again after saving.</p>
        <button data-testid="gw-test-btn" onClick={runTest} disabled={testBusy || !cfg.service_account_set} className="mt-3 inline-flex items-center gap-2 px-3 py-2 rounded text-sm font-title" style={{ background: 'var(--gold)', color: '#3a2c06', opacity: (!cfg.service_account_set || testBusy) ? 0.6 : 1 }}>
          {testBusy ? <Loader2 className="animate-spin" size={14} /> : <PlugZap size={14} />} Test connection
        </button>
        {testResult && (
          <div className="mt-3 text-sm" data-testid="gw-test-result">
            {testResult.ok ? (
              <ul className="space-y-1">
                {Object.entries(testResult.checks || {}).map(([k, v]) => (
                  <li key={k} className="flex items-start gap-2">
                    {typeof v === 'object' ? (v.ok ? <CheckCircle2 size={14} className="mt-0.5 shrink-0" style={{ color: '#1a7f37' }} /> : <XCircle size={14} className="mt-0.5 shrink-0" style={{ color: '#8a1f11' }} />) : <CheckCircle2 size={14} className="mt-0.5 shrink-0" style={{ color: '#1a7f37' }} />}
                    <span className="capitalize">{k}</span>
                    {typeof v === 'object' && v.error && (
                      <span style={{ color: '#8a1f11' }} data-testid={`gw-test-error-${k}`}>— {v.error.split(/(https?:\/\/\S+)/g).map((part, i) => (/^https?:\/\//.test(part)
                        ? <a key={i} href={part} target="_blank" rel="noreferrer" className="underline break-all" style={{ color: 'var(--navy)' }}>{part}</a>
                        : part))}</span>
                    )}
                    {k === 'client_email' && <span style={{ color: 'var(--olive)' }}>{v}</span>}
                  </li>
                ))}
              </ul>
            ) : <span style={{ color: '#8a1f11' }}>{testResult.error}</span>}
          </div>
        )}
      </div>

      {/* Calendars */}
      <div className="bg-white rounded-lg p-5 border shadow-sm" style={cardStyle}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2"><CalendarDays size={16} style={{ color: 'var(--navy)' }} /><h3 className="font-title text-lg" style={{ color: 'var(--navy)' }}>Calendars (tiered)</h3></div>
          <button data-testid="gw-add-calendar" onClick={() => addSource('calendars', 'calendar_id')} className="inline-flex items-center gap-1 text-sm px-2 py-1 rounded font-title" style={{ background: 'var(--navy)', color: 'var(--cream)' }}><Plus size={14} /> Add calendar</button>
        </div>
        {(cfg.calendars || []).length === 0 && <p className="text-sm" style={{ color: 'var(--olive)' }}>No calendars yet. Add one and map which member classes can see it.</p>}
        <div className="space-y-3">
          {(cfg.calendars || []).map((s, i) => (
            <div key={i} className="rounded border p-3" style={{ borderColor: 'rgba(0,0,0,0.12)' }} data-testid={`gw-calendar-row-${i}`}>
              <div className="grid md:grid-cols-2 gap-2">
                <Input data-testid={`gw-calendar-label-${i}`} value={s.label} onChange={(e) => updSource('calendars', i, { label: e.target.value })} placeholder="Label (e.g. Committee Calendar)" />
                <Input data-testid={`gw-calendar-id-${i}`} value={s.calendar_id || ''} onChange={(e) => updSource('calendars', i, { calendar_id: e.target.value })} placeholder="Calendar ID (…@group.calendar.google.com)" />
              </div>
              <div className="mt-2">
                <span className="text-xs" style={{ color: 'var(--olive)' }}>Who can see this calendar?</span>
                <ClassChips selected={s.allowed_classes || []} onToggle={(k) => toggleClass('calendars', i, k)} memberClasses={memberClasses} classLabel={classLabel} testidPrefix={`gw-calendar-${i}`} />
              </div>
              <button data-testid={`gw-calendar-del-${i}`} onClick={() => delSource('calendars', i)} className="mt-2 inline-flex items-center gap-1 text-xs" style={{ color: '#8a1f11' }}><Trash2 size={13} /> Remove</button>
            </div>
          ))}
        </div>
      </div>

      {/* Folders */}
      <div className="bg-white rounded-lg p-5 border shadow-sm" style={cardStyle}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2"><FolderOpen size={16} style={{ color: 'var(--navy)' }} /><h3 className="font-title text-lg" style={{ color: 'var(--navy)' }}>Document folders (tiered)</h3></div>
          <button data-testid="gw-add-folder" onClick={() => addSource('folders', 'folder_id')} className="inline-flex items-center gap-1 text-sm px-2 py-1 rounded font-title" style={{ background: 'var(--navy)', color: 'var(--cream)' }}><Plus size={14} /> Add folder</button>
        </div>
        {(cfg.folders || []).length === 0 && <p className="text-sm" style={{ color: 'var(--olive)' }}>No folders yet. Add one and map which member classes can see it.</p>}
        <div className="space-y-3">
          {(cfg.folders || []).map((s, i) => (
            <div key={i} className="rounded border p-3" style={{ borderColor: 'rgba(0,0,0,0.12)' }} data-testid={`gw-folder-row-${i}`}>
              <div className="grid md:grid-cols-2 gap-2">
                <Input data-testid={`gw-folder-label-${i}`} value={s.label} onChange={(e) => updSource('folders', i, { label: e.target.value })} placeholder="Label (e.g. Committee Documents)" />
                <Input data-testid={`gw-folder-id-${i}`} value={s.folder_id || ''} onChange={(e) => updSource('folders', i, { folder_id: e.target.value })} placeholder="Drive folder ID" />
              </div>
              <div className="mt-2">
                <span className="text-xs" style={{ color: 'var(--olive)' }}>Who can see this folder?</span>
                <ClassChips selected={s.allowed_classes || []} onToggle={(k) => toggleClass('folders', i, k)} memberClasses={memberClasses} classLabel={classLabel} testidPrefix={`gw-folder-${i}`} />
              </div>
              <button data-testid={`gw-folder-del-${i}`} onClick={() => delSource('folders', i)} className="mt-2 inline-flex items-center gap-1 text-xs" style={{ color: '#8a1f11' }}><Trash2 size={13} /> Remove</button>
            </div>
          ))}
        </div>
      </div>

      {/* Sheet */}
      <div className="bg-white rounded-lg p-5 border shadow-sm" style={cardStyle}>
        <div className="flex items-center gap-2 mb-3"><Table2 size={16} style={{ color: 'var(--navy)' }} /><h3 className="font-title text-lg" style={{ color: 'var(--navy)' }}>Google Sheet — members data source</h3></div>
        <div className="grid md:grid-cols-2 gap-2">
          <div>
            <label className="text-xs" style={{ color: 'var(--olive)' }}>Spreadsheet ID</label>
            <Input data-testid="gw-sheet-id" value={cfg.sheet_id || ''} onChange={(e) => setField('sheet_id', e.target.value)} placeholder="The part of the URL between /d/ and /edit" />
          </div>
          <div>
            <label className="text-xs" style={{ color: 'var(--olive)' }}>Range / tab</label>
            <div className="flex gap-2">
              <Input data-testid="gw-sheet-range" value={cfg.members_range || ''} onChange={(e) => setField('members_range', e.target.value)} placeholder="Members!A:Z" />
              <button data-testid="gw-sheet-tabs-btn" onClick={loadTabs} disabled={tabsBusy || !cfg.sheet_id || !cfg.service_account_set} title="List the tabs in this spreadsheet" className="inline-flex items-center gap-1 px-3 rounded text-xs font-title whitespace-nowrap" style={{ background: 'var(--sage)', color: 'var(--ink)', opacity: (tabsBusy || !cfg.sheet_id || !cfg.service_account_set) ? 0.6 : 1 }}>
                {tabsBusy ? <Loader2 className="animate-spin" size={13} /> : <Table2 size={13} />} Find tabs
              </button>
            </div>
            {tabs && (
              <div className="mt-1.5 text-xs" data-testid="gw-sheet-tabs">
                {tabs.error ? <span style={{ color: '#8a1f11' }}>{tabs.error}</span> : (
                  <span className="flex flex-wrap items-center gap-1.5">
                    <span style={{ color: 'var(--olive)' }}>Tabs found — click one to use it:</span>
                    {tabs.tabs.map((t) => (
                      <button key={t} data-testid={`gw-sheet-tab-${t}`} onClick={() => setField('members_range', `${/[^A-Za-z0-9_]/.test(t) ? `'${t}'` : t}!A:Z`)} className="px-2 py-0.5 rounded font-title" style={{ background: (cfg.members_range || '').replace(/'/g, '').startsWith(`${t}!`) ? 'var(--navy)' : 'white', color: (cfg.members_range || '').replace(/'/g, '').startsWith(`${t}!`) ? 'var(--cream)' : 'var(--ink)', border: '1px solid rgba(0,0,0,0.18)' }}>{t}</button>
                    ))}
                    <span style={{ color: 'var(--olive)' }}>then Save.</span>
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
        <div className="flex flex-wrap gap-2 mt-3">
          <button data-testid="gw-sheet-preview" onClick={runPreview} disabled={previewBusy || !cfg.sheet_id || !cfg.service_account_set} className="inline-flex items-center gap-2 px-3 py-2 rounded text-sm font-title" style={{ background: 'var(--sage)', color: 'var(--ink)', opacity: (previewBusy || !cfg.sheet_id || !cfg.service_account_set) ? 0.6 : 1 }}>
            {previewBusy ? <Loader2 className="animate-spin" size={14} /> : <Eye size={14} />} Preview sheet
          </button>
        </div>
        <SheetSyncControls cfg={cfg} setField={setField} />
        <div className="flex items-center gap-3 mt-3">
          <Switch data-testid="gw-autosync-toggle" checked={!!cfg.auto_sync_members} onCheckedChange={(v) => setField('auto_sync_members', v)} />
          <span className="text-sm">Nightly auto-sync — run the sync above automatically each day (then Save)</span>
        </div>
        {preview && (
          <div className="mt-3 overflow-x-auto" data-testid="gw-preview-result">
            {preview.error ? <p className="text-sm" style={{ color: '#8a1f11' }}>{preview.error}</p> : (
              <>
                <p className="text-xs mb-1" style={{ color: 'var(--olive)' }}>{preview.total} data rows found. Showing first {preview.rows.length}.</p>
                <table className="text-xs border-collapse">
                  <thead><tr>{preview.headers.map((h, i) => <th key={i} className="border px-2 py-1 text-left" style={{ background: 'var(--navy)', color: 'var(--cream)', borderColor: 'rgba(0,0,0,0.1)' }}>{h}</th>)}</tr></thead>
                  <tbody>{preview.rows.map((r, i) => <tr key={i}>{preview.headers.map((_, j) => <td key={j} className="border px-2 py-1" style={{ borderColor: 'rgba(0,0,0,0.1)' }}>{r[j] || ''}</td>)}</tr>)}</tbody>
                </table>
              </>
            )}
          </div>
        )}
      </div>

      <GoogleContactsCard cfg={cfg} setField={setField} />
      <SyncAlertsCard cfg={cfg} setField={setField} />

      {/* Domain sign-in */}
      <div className="bg-white rounded-lg p-5 border shadow-sm" style={cardStyle}>
        <h3 className="font-title text-lg mb-3" style={{ color: 'var(--navy)' }}>Workspace domain sign-in</h3>
        <div className="flex items-center gap-3 mb-3">
          <Switch data-testid="gw-promote-toggle" checked={!!cfg.domain_promote_enabled} onCheckedChange={(v) => setField('domain_promote_enabled', v)} />
          <span className="text-sm">Auto-promote members who sign in with a Workspace domain email</span>
        </div>
        {cfg.domain_promote_enabled && (
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <label className="text-xs" style={{ color: 'var(--olive)' }}>Domain</label>
              <Input data-testid="gw-promote-domain" value={cfg.promote_domain || ''} onChange={(e) => setField('promote_domain', e.target.value)} placeholder="rae.org.au" />
            </div>
            <div>
              <label className="text-xs" style={{ color: 'var(--olive)' }}>Promote to class</label>
              <Select value={cfg.promote_class || ''} onValueChange={(v) => setField('promote_class', v)}>
                <SelectTrigger data-testid="gw-promote-class"><SelectValue placeholder="Choose a class" /></SelectTrigger>
                <SelectContent>
                  {memberClasses.filter((m) => m.key !== 'public').map((m) => <SelectItem key={m.key} value={m.key}>{classLabel(m.key)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}
        <p className="text-xs mt-2" style={{ color: 'var(--olive)' }}>Never downgrades an existing higher class. Login itself stays open to any Google account.</p>
      </div>

      {err && <p className="text-sm" style={{ color: '#8a1f11' }} data-testid="gw-error">{err}</p>}
      <div className="flex items-center gap-3">
        <button data-testid="gw-save-btn" onClick={save} disabled={busy} className="inline-flex items-center gap-2 px-5 py-2.5 rounded font-title" style={{ background: 'var(--maroon)', color: 'var(--cream)', opacity: busy ? 0.7 : 1 }}>
          {busy ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />} Save Google settings
        </button>
        {saved && <span className="text-sm inline-flex items-center gap-1" style={{ color: '#1a7f37' }}><CheckCircle2 size={15} /> Saved</span>}
      </div>
    </div>
  );
}
